import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  orderBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuthRefresh?: boolean;
}

class ApiClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true, // Include cookies for session handling
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private isRefreshing = false;
  private refreshAttempts = 0;
  private maxRefreshAttempts = 1; // Reduced to 1 to prevent excessive retries
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];

  private shouldAttemptTokenRefresh(error: AxiosError): boolean {
    if (error.response?.status !== 401) {
      return false;
    }

    const requestConfig = error.config as ApiRequestConfig | undefined;
    if (requestConfig?.skipAuthRefresh) {
      return false;
    }

    const requestUrl = requestConfig?.url || '';
    const publicAuthPaths = ['/auth/login', '/auth/refresh', '/auth/guest', '/auth/verify-code'];

    return !publicAuthPaths.some((path) => requestUrl.includes(path));
  }

  private async handleTokenRefresh(originalError: AxiosError): Promise<any> {
    const authData = JSON.parse(localStorage.getItem('dasoonegg-auth') || '{}');
    const refreshToken = authData.state?.refreshToken;
    
    console.log('Token refresh attempt:', { hasRefreshToken: !!refreshToken, isRefreshing: this.isRefreshing, attempts: this.refreshAttempts });
    
    // Prevent infinite refresh loops
    if (this.refreshAttempts >= this.maxRefreshAttempts) {
      console.log('Max refresh attempts reached, forcing logout');
      localStorage.removeItem('dasoonegg-auth');
      window.location.href = '/login';
      return Promise.reject(originalError);
    }
    
    if (!refreshToken) {
      // No refresh token, must login again
      console.log('No refresh token available, redirecting to login');
      localStorage.removeItem('dasoonegg-auth');
      window.location.href = '/login';
      return Promise.reject(originalError);
    }

    if (this.isRefreshing) {
      console.log('Token refresh in progress, queueing request');
      // Token refresh is already in progress, queue this request
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then(() => {
        // Retry the original request after token refresh
        return this.instance.request(originalError.config!);
      });
    }

    this.isRefreshing = true;
    this.refreshAttempts++;

    try {
      console.log('Starting token refresh...');
      
      // Create a new axios instance for refresh to avoid interceptors
      const refreshResponse = await axios.post(`${this.instance.defaults.baseURL}/auth/refresh`, {
        refreshToken
      }, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('Refresh response:', refreshResponse.data);

      if (refreshResponse.data.success && refreshResponse.data.data) {
        const { token: newToken, refreshToken: newRefreshToken } = refreshResponse.data.data;
        
        console.log('Token refresh successful, updating storage');
        
        // Update stored auth data
        const updatedAuthData = {
          ...authData,
          state: {
            ...authData.state,
            token: newToken,
            refreshToken: newRefreshToken
          }
        };
        localStorage.setItem('dasoonegg-auth', JSON.stringify(updatedAuthData));

        // Update auth store if available
        try {
          const { useAuthStore } = await import('../stores/authStore');
          useAuthStore.getState().updateTokens({ token: newToken, refreshToken: newRefreshToken });
        } catch (error) {
          console.warn('Failed to update auth store:', error);
        }

        // Update default header for future requests
        this.instance.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        // Note: Don't reset refresh attempts yet - only reset after confirming the new token works

        // Process queued requests
        this.failedQueue.forEach(({ resolve }) => resolve(newToken));
        this.failedQueue = [];

        // Retry the original request with new token
        if (originalError.config) {
          originalError.config.headers!.Authorization = `Bearer ${newToken}`;
          console.log('Retrying original request with new token');
          
          try {
            const retryResponse = await this.instance.request(originalError.config);
            // If retry succeeds, reset refresh attempts
            this.refreshAttempts = 0;
            console.log('Retry successful, reset refresh attempts');
            return retryResponse;
          } catch (retryError) {
            console.log('Retry failed, keeping refresh attempts count:', this.refreshAttempts);
            // Don't reset attempts - let it accumulate to eventually hit the max limit
            throw retryError;
          }
        }
      } else {
        throw new Error('Token refresh response invalid');
      }
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      
      // Refresh failed, clear auth and redirect to login
      this.failedQueue.forEach(({ reject }) => reject(refreshError));
      this.failedQueue = [];
      
      localStorage.removeItem('dasoonegg-auth');
      window.location.href = '/login';
      return Promise.reject(originalError);
    } finally {
      this.isRefreshing = false;
    }
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('dasoonegg-auth');
        if (token) {
          const authData = JSON.parse(token);
          if (authData.state?.token) {
            config.headers.Authorization = `Bearer ${authData.state.token}`;
          }
        }

        // Add request timestamp for debugging
        if (import.meta.env.VITE_NODE_ENV === 'development') {
          console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (import.meta.env.VITE_NODE_ENV === 'development') {
          console.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data,
          });
        }

        return response;
      },
      (error: AxiosError) => {
        if (import.meta.env.VITE_NODE_ENV === 'development') {
          console.error('[API Response Error]', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
          });
        }

        // Handle specific error cases
        if (this.shouldAttemptTokenRefresh(error)) {
          // Unauthorized - try to refresh token before logging out
          return this.handleTokenRefresh(error);
        } else if (error.response && error.response.status === 403) {
          // Forbidden - show access denied message
          console.warn('Access denied:', error.response.data);
        } else if (error.response && error.response.status >= 500) {
          // Server error - show generic error message
          console.error('Server error:', error.response?.data || error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  // Generic GET request with deduplication and circuit breaker
  async get<T>(url: string, params?: Record<string, any>, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    // Import utilities dynamically to avoid circular dependencies
    const { requestDeduplicator } = await import('../utils/requestDeduplicator');
    const { circuitBreaker } = await import('../utils/circuitBreaker');
    const { rateLimiter } = await import('../utils/rateLimiter');
    
    // Check circuit breaker with endpoint awareness
    if (!circuitBreaker.canExecute(url)) {
      throw new Error('Circuit breaker is open - too many failures');
    }
    
    // Check rate limiter
    const rateLimitKey = this.getRateLimitKey(url);
    if (!rateLimiter.canMakeCall(rateLimitKey)) {
      throw new Error('Rate limit exceeded - please try again later');
    }
    
    // Create unique key for this request
    const requestKey = `GET:${url}:${JSON.stringify(params || {})}`;
    
    // Skip deduplication for auth-critical endpoints
    const skipDedup = url.includes('/auth/verify-code') || url.includes('/auth/refresh');
    
    return requestDeduplicator.dedupe(requestKey, async () => {
      try {
        const response = await this.instance.get<ApiResponse<T>>(url, { ...config, params });
        circuitBreaker.recordSuccess();
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 429) {
          console.error('[API] 429 Rate limit hit, recording circuit breaker failure');
          circuitBreaker.recordFailure();
        }
        throw error;
      }
    }, { skipDedup });
  }

  // GET a binary/file response (CSV, PDF, ...). Unlike `get`, this returns the raw
  // response body untouched instead of unwrapping an ApiResponse envelope, because
  // file endpoints send the file itself rather than `{ success, data }`.
  // Downloads are user-initiated and must always hit the network, so deduplication
  // is skipped; auth and token refresh still come from the shared interceptors.
  async getBlob(url: string, params?: Record<string, any>, config?: ApiRequestConfig): Promise<Blob> {
    const { circuitBreaker } = await import('../utils/circuitBreaker');
    const { rateLimiter } = await import('../utils/rateLimiter');

    if (!circuitBreaker.canExecute(url)) {
      throw new Error('Circuit breaker is open - too many failures');
    }

    const rateLimitKey = this.getRateLimitKey(url);
    if (!rateLimiter.canMakeCall(rateLimitKey)) {
      throw new Error('Rate limit exceeded - please try again later');
    }

    try {
      const response = await this.instance.get(url, { ...config, params, responseType: 'blob' });
      circuitBreaker.recordSuccess();
      return response.data as Blob;
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.error('[API] 429 Rate limit hit, recording circuit breaker failure');
        circuitBreaker.recordFailure();
      }
      throw error;
    }
  }

  // Generic POST request
  async post<T>(url: string, data?: any, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    // Import utilities dynamically to avoid circular dependencies
    const { circuitBreaker } = await import('../utils/circuitBreaker');
    const { rateLimiter } = await import('../utils/rateLimiter');
    
    // Check circuit breaker with endpoint awareness
    if (!circuitBreaker.canExecute(url)) {
      throw new Error('Circuit breaker is open - too many failures');
    }
    
    // Check rate limiter
    const rateLimitKey = this.getRateLimitKey(url);
    if (!rateLimiter.canMakeCall(rateLimitKey)) {
      throw new Error('Rate limit exceeded - please try again later');
    }
    
    try {
      const response = await this.instance.post<ApiResponse<T>>(url, data, config);
      circuitBreaker.recordSuccess();
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.error('[API] 429 Rate limit hit, recording circuit breaker failure');
        circuitBreaker.recordFailure();
      }
      throw error;
    }
  }

  // Generic PUT request
  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  // Generic PATCH request
  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<ApiResponse<T>>(url, data);
    return response.data;
  }

  // Generic DELETE request
  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url);
    return response.data;
  }

  // Upload file
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.instance.post<ApiResponse<T>>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  }

  // Set auth token
  setAuthToken(token: string): void {
    this.instance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  // Clear auth token
  clearAuthToken(): void {
    delete this.instance.defaults.headers.common.Authorization;
  }

  // Reset all rate limiting and circuit breaker states for new session
  async resetAllStates(): Promise<void> {
    console.log('[ApiClient] Resetting all states for new session');
    
    try {
      const { circuitBreaker } = await import('../utils/circuitBreaker');
      const { rateLimiter } = await import('../utils/rateLimiter');
      const { requestDeduplicator } = await import('../utils/requestDeduplicator');
      
      // Reset all utilities
      circuitBreaker.resetForNewSession();
      rateLimiter.resetSession();
      requestDeduplicator.clearSession();
      
      // Reset refresh attempts
      this.refreshAttempts = 0;
      this.isRefreshing = false;
      this.failedQueue = [];
      
      console.log('[ApiClient] All states reset successfully');
    } catch (error) {
      console.error('[ApiClient] Error resetting states:', error);
    }
  }

  // Get rate limit key based on endpoint
  private getRateLimitKey(url: string): string {
    if (url.includes('/auth/')) {
      if (url.includes('/refresh')) return 'token-refresh';
      if (url.includes('/verify-code')) return 'captcha';
      return 'auth';
    }
    return 'default';
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string; uptime: number }>> {
    return this.get('/health');
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export as 'api' for backward compatibility
export const api = apiClient;

// Helper function to handle API errors
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiResponse<unknown>;
    return apiError?.error || apiError?.message || error.message;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

// Helper function to extract data from API response
export const extractData = <T>(response: ApiResponse<T>): T => {
  if (!response.success) {
    throw new Error(response.error || response.message || 'API request failed');
  }
  
  if (response.data === undefined || response.data === null) {
    throw new Error('No data received from API');
  }
  
  return response.data;
};
